import { Banknote, Crown, Zap, ArrowRight, TrendingUp, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Royal Empowerment Panel — wallet · tier · instant-pay
 *
 * Driven by GET /providers/me/royal-empowerment response.
 * Shape:
 *   {
 *     tier: "bronze" | "silver" | "gold",
 *     tier_label, commission_pct, next_tier, next_tier_at_jobs,
 *     next_tier_progress: { current, needed, remaining, percent },
 *     completed_jobs,
 *     instant_pay: { available, min_amount, eligible_amount, fast_fee, net_payout, pending_request },
 *     wallet: { pending, paid_lifetime, last_payout, bank_details, ... }
 *   }
 */
const TIER_STYLE = {
    bronze: {
        bg: "from-[#5a3a1a]/40 via-card to-card",
        border: "border-amber-900/40",
        chip: "text-amber-300 bg-amber-900/30",
    },
    silver: {
        bg: "from-slate-500/30 via-card to-card",
        border: "border-slate-400/40",
        chip: "text-slate-200 bg-slate-500/30",
    },
    gold: {
        bg: "from-[#D4AF37]/30 via-card to-card",
        border: "border-primary/50",
        chip: "text-primary bg-primary/20",
    },
};

export default function RoyalEmpowermentPanel({ data, onRequestInstantPay }) {
    const { tier = "bronze", tier_label, commission_pct, next_tier, completed_jobs, next_tier_progress, instant_pay, wallet } = data || {};
    const style = TIER_STYLE[tier] || TIER_STYLE.bronze;
    const pending = wallet?.pending || 0;
    const lifetime = wallet?.paid_lifetime || 0;

    const ip = instant_pay || {};
    const pendingRequest = ip.pending_request;

    return (
        <section
            className={`relative overflow-hidden rounded-md border ${style.border} bg-gradient-to-br ${style.bg} p-6 sm:p-8`}
            data-testid="royal-empowerment-panel"
        >
            <div className="absolute -right-10 -top-10 opacity-10 pointer-events-none">
                <Crown size={180} className="text-primary" strokeWidth={0.6} />
            </div>

            <div className="relative flex flex-col gap-6">
                {/* Header row */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Crown size={12} className="gold-text" strokeWidth={1.5} />
                            <span className="overline">Royal Empowerment</span>
                        </div>
                        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight" data-testid="empowerment-title">
                            Your Royal Wallet
                        </h2>
                    </div>
                    <div
                        className={`px-3 py-2 rounded-sm border ${style.border} ${style.chip} text-[10px] uppercase tracking-[0.25em] font-semibold flex items-center gap-2`}
                        data-testid={`tier-badge-${tier}`}
                    >
                        <Crown size={11} strokeWidth={1.5} />
                        {tier_label} · {commission_pct}% commission
                    </div>
                </div>

                {/* Wallet stats */}
                <div className="grid sm:grid-cols-3 gap-4">
                    <Stat
                        label="Pending payout"
                        value={`$${pending.toLocaleString()}`}
                        sub="Next batch"
                        Icon={Clock}
                        testid="wallet-pending"
                    />
                    <Stat
                        label="Lifetime earned"
                        value={`$${lifetime.toLocaleString()}`}
                        sub={`${completed_jobs} jobs delivered`}
                        Icon={TrendingUp}
                        testid="wallet-lifetime"
                    />
                    <Stat
                        label="Commission rate"
                        value={`${commission_pct}%`}
                        sub={next_tier ? `${next_tier} unlocks at ${data.next_tier_at_jobs} jobs` : "Top tier — max benefits"}
                        Icon={Banknote}
                        testid="wallet-commission"
                    />
                </div>

                {/* Tier progress */}
                {next_tier_progress && next_tier && (
                    <div className="p-4 border border-white/10 rounded-sm bg-black/30" data-testid="tier-progress">
                        <div className="flex items-center justify-between text-xs mb-2">
                            <span className="text-muted-foreground">
                                <strong className="text-foreground">{next_tier_progress.remaining}</strong> jobs to reach{" "}
                                <span className="gold-text uppercase tracking-[0.2em]">{next_tier}</span>
                            </span>
                            <span className="gold-text font-semibold tabular-nums">
                                {next_tier_progress.percent}%
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#8E7100] via-[#D4AF37] to-[#FFD86B] transition-all duration-700"
                                style={{ width: `${next_tier_progress.percent}%` }}
                                data-testid="tier-progress-fill"
                            />
                        </div>
                        <p className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
                            Bronze (≤ 10 jobs) pays 20% Royal Protection. Silver (11–50) drops to 15%. Gold (51+) is just 10% — the lowest commission on the island.
                        </p>
                    </div>
                )}

                {/* Instant Pay CTA */}
                <div className="p-5 border border-primary/30 rounded-sm bg-primary/5" data-testid="instant-pay-block">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                                <Zap size={14} className="gold-text" strokeWidth={1.5} fill="currentColor" />
                                <span className="text-sm font-semibold gold-text uppercase tracking-[0.2em]">
                                    Instant Pay
                                </span>
                            </div>
                            {pendingRequest ? (
                                <p className="text-sm text-foreground" data-testid="instant-pay-pending-msg">
                                    Request for <strong className="gold-text">${pendingRequest.net_payout_jmd?.toLocaleString()}</strong> submitted &mdash;
                                    we&apos;ll wire it within 30 minutes during business hours.
                                </p>
                            ) : ip.available ? (
                                <p className="text-sm text-foreground" data-testid="instant-pay-quote">
                                    Cash out <strong className="gold-text">${ip.eligible_amount?.toLocaleString()}</strong>{" "}
                                    now — fast fee <strong className="text-amber-300">${ip.fast_fee?.toLocaleString()}</strong>{" "}
                                    · you receive <strong className="gold-text">${ip.net_payout?.toLocaleString()}</strong>.
                                </p>
                            ) : (
                                <p className="text-sm text-muted-foreground" data-testid="instant-pay-locked-msg">
                                    Earn at least <strong className="text-foreground">${ip.min_amount?.toLocaleString() || "500"}</strong> in pending balance to unlock Instant Pay.
                                </p>
                            )}
                        </div>
                        <Button
                            disabled={!ip.available || !!pendingRequest}
                            onClick={onRequestInstantPay}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-[11px] h-11 px-6"
                            data-testid="instant-pay-btn"
                        >
                            {pendingRequest ? "Requested" : "Cash out now"}
                            <ArrowRight size={13} className="ml-2" strokeWidth={1.5} />
                        </Button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value, sub, Icon, testid }) {
    return (
        <div className="p-4 bg-black/30 border border-white/10 rounded-sm" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                {Icon && <Icon size={12} className="gold-text" strokeWidth={1.5} />}
                <span className="overline">{label}</span>
            </div>
            <div className="font-serif text-2xl tabular-nums" data-testid={`${testid}-value`}>
                {value}
            </div>
            {sub && <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{sub}</div>}
        </div>
    );
}
